import React, { useState } from 'react';
import styled from 'styled-components';
import {
  minBreakpointQuery,
  maxBreakpointQuery,
  standardColours,
  brandColours,
  fontSize,
  zIndexLayers,
  standardTransition,
  visuallyHidden,
} from '../styles';
import { Container, Slider, Image, Svg } from './ui';
import galleryIcon from '../images/gallery-icon.svg';

const StyledSpaGallery = styled.section``;

const StyledItems = styled.div`
  ${maxBreakpointQuery.smedium`
    ${visuallyHidden()};
  `}

  ${minBreakpointQuery.smedium`
    position: relative;
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 12px;
  `}
`;

const StyledImage = styled(Image)`
  &:first-child {
    ${minBreakpointQuery.smedium`
      grid-row: 1/3;
      grid-column: 1/3;
    `}
  }
`;

const StyledTag = styled.p`
  position: absolute;
  top: 0;
  left: 0;
  display: inline-block;
  padding: 12px 20px;
  color: ${standardColours.white};
  background-color: ${brandColours.primary};

  ${minBreakpointQuery.small`
    top: 15px;
    ${fontSize(18)};
  `}

  ${minBreakpointQuery.large`
    top: 20px;
    ${fontSize(20)};
  `}
`;

const StyledButton = styled.button`
  ${maxBreakpointQuery.smedium`
    ${visuallyHidden()};
  `}

  ${minBreakpointQuery.smedium`
    position: absolute;
    right: 20px;
    bottom: 20px;
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 12px;
    padding: 14px 20px 14px 12px;
    background-color: ${standardColours.transparentWhite(0.95)};
    border-radius: 10px;
  `}
`;

const StyledSliderOuter = styled.div`
  ${minBreakpointQuery.smedium`
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    overflow: auto;
    z-index: ${zIndexLayers.fifth};
    display: flex;
    background-color: ${brandColours.primary};
    transform: ${({ showSlider }) =>
      showSlider ? 'translateY(0)' : 'translateY(-100%)'};
    transition: ${standardTransition('transform', '0.4s')};
  `}
`;

const StyledSliderContainer = styled(Container)`
  ${maxBreakpointQuery.smedium`
    padding-right: 0;
    padding-left: 0;
  `}

  ${minBreakpointQuery.smedium`
    margin: auto;
    max-width: 1060px;
  `}
`;

const StyledSliderInner = styled.div`
  position: relative;

  ${minBreakpointQuery.smedium`
    padding-top: 50px;
    padding-bottom: 50px;
  `}
`;

const StyledPositionClose = styled.div`
  position: absolute;
  top: 20px;
  left: 20px;
  z-index: 1;
  display: flex;
  justify-content: space-between;
  align-items: center;

  ${minBreakpointQuery.smedium`
    top: 10px;
    left: 0;
    width: 100%;
  `}
`;

const StyledPosition = styled.span`
  color: ${standardColours.white};
  ${fontSize(14)};
  letter-spacing: 2px;

  ${maxBreakpointQuery.smedium`
    padding: 4px 8px;
    background-color: ${standardColours.transparentBlack(0.5)};
    border-radius: 3px;
  `}

  ${minBreakpointQuery.small`
    ${fontSize(16)};
  `}

  ${minBreakpointQuery.large`
    ${fontSize(18)};
  `}
`;

const StyledClose = styled.button`
  ${maxBreakpointQuery.smedium`
    ${visuallyHidden()};
  `}

  ${minBreakpointQuery.smedium`
    display: block;
    padding: 4px;
  `}

  span {
    ${minBreakpointQuery.smedium`
      ${visuallyHidden()};
    `}
  }

  &:after {
    ${minBreakpointQuery.smedium`
      content: '✕';
      color: ${standardColours.white};
      ${fontSize(26)};
      line-height: 1;
    `}
  }
`;

const StyledSlider = styled(Slider)`
  .slick-arrow {
    ${maxBreakpointQuery.smedium`
      ${visuallyHidden()};
    `}

    ${minBreakpointQuery.smedium`
      top: 50%;
      transform: translateY(-50%);
    `}

    &.slick-prev {
      ${minBreakpointQuery.smedium`
        left: 10px;
      `}

      ${minBreakpointQuery.large`
        left: -16px;
      `}
    }

    &.slick-next {
      ${minBreakpointQuery.smedium`
        right: 10px;
      `}

      ${minBreakpointQuery.large`
        right: -16px;
      `}
    }
  }

  .slick-track {
    display: flex;
    align-items: center;
  }

  .slick-dots {
    bottom: 20px;
    width: 100%;
    font-size: 0;
    text-align: center;

    ${minBreakpointQuery.smedium`
      bottom: -30px;
    `}
  }
`;

const StyledSliderImage = styled(Image)`
  pointer-events: none;

  ${maxBreakpointQuery.smedium`
    aspect-ratio: 3/2;
    object-fit: cover;
  `}

  ${minBreakpointQuery.smedium`
    object-fit: contain;
  `}
`;

const SpaGallery = ({ images, isLuxury }) => {
  const [showSlider, setShowSlider] = useState(false);
  const [currentSlideNumber, setCurrentSlideNumber] = useState(1);

  const sliderOptions = {
    arrowSize: 'large',
    dots: true,
    dotsAltColours: true,
    beforeChange: (oldIndex, newIndex) => {
      setCurrentSlideNumber(newIndex + 1);
    },
  };

  return (
    <>
      {images && (
        <StyledSpaGallery id="spa-gallery">
          <Container>
            <StyledItems>
              {images.slice(0, 5).map(({ url, title }, i) => (
                <StyledImage
                  key={i}
                  src={url}
                  alt={title}
                  width={i === 0 ? 585 : 290}
                  height={i === 0 ? 505 : 250}
                  layout="constrained"
                  loading="eager"
                  fetchpriority="high"
                />
              ))}
              {isLuxury && <StyledTag>Luxury Spa</StyledTag>}
              <StyledButton onClick={() => setShowSlider(true)}>
                <Svg image={galleryIcon} />
                view all photos
              </StyledButton>
            </StyledItems>
          </Container>
          <StyledSliderOuter showSlider={showSlider}>
            <StyledSliderContainer>
              <StyledSliderInner>
                <StyledPositionClose>
                  <StyledPosition>
                    {currentSlideNumber}/{images.length}
                  </StyledPosition>
                  <StyledClose
                    title="Close"
                    onClick={() => setShowSlider(false)}
                  >
                    <span>Close</span>
                  </StyledClose>
                </StyledPositionClose>
                <StyledSlider {...sliderOptions}>
                  {images.map(({ url, title, width, height }, i) => (
                    <StyledSliderImage
                      key={i}
                      src={url}
                      alt={title}
                      height={680}
                      sourceWidth={width}
                      sourceHeight={height}
                      layout="constrained"
                      objectFit="inherit"
                    />
                  ))}
                </StyledSlider>
              </StyledSliderInner>
            </StyledSliderContainer>
          </StyledSliderOuter>
        </StyledSpaGallery>
      )}
    </>
  );
};

export default SpaGallery;
